import { useCallback, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress, Container, Stack, Switch, FormControlLabel, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppShellNav from '../../shell/AppShellNav';
import api from '../../store/api';
import { permissionGroups, permissionStatus, toolPresentation } from './mcpAccessPresentation';
import '../../shell/appShell.css';

const ROOT = '/users/me/mcp-access';
const confirmation = { headers: { 'X-MCP-Confirm': 'true' } };
const accessDescription = tool => tool.access === 'write'
  ? 'Puede crear o actualizar los datos que autorices; las acciones se registran.'
  : 'Solo lectura; no modifica datos.';

function ToolRow({ tool, publicTool = false, disabled, onChange }) {
  const { title, description } = toolPresentation(tool);
  return (
    <Box sx={{ py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" component="h4" fontWeight={600}>{title}</Typography>
          <Typography variant="body2" color="text.secondary">{description}</Typography>
          {!publicTool && <Chip size="small" sx={{ mt: 1, maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
            label={permissionStatus(tool)} color={!tool.blockedReason && tool.available ? 'success' : 'default'} />}
          {tool.blockedReason === 'reconsent_required' && <Typography variant="body2" sx={{ mt: 1 }}>Revisa los detalles y acepta la versión {tool.consentVersion}.</Typography>}
          {tool.blockedReason === 'scope_missing' && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            El permiso de DevsSoft y la autorización del conector son independientes. Vuelve a conectar el complemento con los permisos necesarios.
          </Typography>}
        </Box>
        {!publicTool && !tool.base && <Switch checked={!!tool.granted} disabled={disabled}
          inputProps={{ 'aria-label': 'Permitir ' + title }} onChange={(_, granted) => onChange([tool], granted)} />}
      </Stack>
      {tool.base && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Disponible mientras el acceso MCP esté activo.</Typography>}
      <Box component="details" role="group" aria-label={'Detalles de ' + title} sx={{ mt: 1, '& summary': { cursor: 'pointer', color: 'primary.main', fontSize: '0.875rem', width: 'fit-content', py: 0.5 } }}>
        <Box component="summary" aria-label={'Ver detalles de ' + title}>Ver detalles</Box>
        <Stack spacing={1} sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body2"><strong>Para qué sirve:</strong> {description}</Typography>
          <Typography variant="body2"><strong>Datos:</strong> {tool.data.join(', ')}. {accessDescription(tool)}</Typography>
          {tool.examples?.[0] && <Typography variant="body2"><strong>Ejemplo:</strong> {tool.examples[0]}</Typography>}
        </Stack>
      </Box>
    </Box>
  );
}

function PermissionSection({ serverId, accessType, tools, access, busy, onChange }) {
  if (!tools.length) return null;
  const title = accessType === 'write' ? 'Crear y actualizar' : 'Consultar datos';
  const description = accessType === 'write' ? 'Puede modificar los datos que autorices; las acciones quedan registradas.' : 'Solo lectura; no modifica datos.';
  const authorized = tools.filter(tool => tool.granted).length;
  return (
    <Accordion disableGutters variant="outlined" TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary aria-label={title} expandIcon={<ExpandMoreIcon />} id={serverId + '-' + accessType + '-header'} aria-controls={serverId + '-' + accessType + '-content'}>
        <Box>
          <Typography component="h3" variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">{authorized} de {tools.length} autorizadas · {description}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails id={serverId + '-' + accessType + '-content'}>
        {permissionGroups(tools).map(group => {
          const eligible = group.tools.filter(tool => tool.available && access.linked && access.enabled);
          const selected = eligible.filter(tool => tool.granted).length;
          const allSelected = eligible.length > 0 && selected === eligible.length;
          const data = [...new Set(group.tools.flatMap(tool => tool.data))].join(', ');
          return (
            <Box key={group.name} sx={{ mb: 2 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Typography component="h4" variant="subtitle1" fontWeight={700}>{group.name}</Typography>
                <FormControlLabel sx={{ mr: 0 }} label="Permitir todas" control={
                  <Checkbox checked={allSelected} indeterminate={selected > 0 && !allSelected} disabled={busy || !eligible.length}
                    inputProps={{ 'aria-label': 'Permitir todas las funciones de ' + group.name + ' en ' + title }}
                    onChange={() => onChange(eligible.filter(tool => !!tool.granted === allSelected), !allSelected)} />
                } />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Datos: {data}</Typography>
              {group.tools.map(tool => <ToolRow key={tool.id} tool={tool} disabled={busy || !access.linked || !access.enabled || !tool.available} onChange={onChange} />)}
            </Box>
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}

export default function McpAccessPanel() {
  const [access, setAccess] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const callbackHandled = useRef(false);
  const load = useCallback(async () => {
    try { const { data } = await api.get(ROOT); setAccess(data); }
    catch (e) { setError(e.response?.data?.message || 'No se pudo consultar el acceso MCP.'); }
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('code') && !params.has('error')) { load(); return; }
    if (callbackHandled.current) return;
    callbackHandled.current = true;
    const state = sessionStorage.getItem('mcp-link-state');
    sessionStorage.removeItem('mcp-link-state');
    window.history.replaceState(window.history.state, '', window.location.pathname);
    if (params.has('error') || !state || state !== params.get('state')) {
      setError('No se completó la vinculación. Inicia el proceso nuevamente.'); load(); return;
    }
    setBusy(true);
    api.post(ROOT + '/link/complete', { code: params.get('code'), state }, confirmation)
      .then(() => setMessage('Google vinculado. Activa MCP y concede las funciones que desees.'))
      .catch(e => setError(e.response?.data?.message || 'No se pudo vincular Google.'))
      .finally(() => { setBusy(false); load(); });
  }, [load]);

  async function mutate(path, body, prompt, method = 'put') {
    if (busy || !window.confirm(prompt)) return;
    setBusy(true); setError(''); setMessage('');
    try {
      const response = method === 'delete' ? await api.delete(ROOT + path, confirmation) : await api[method](ROOT + path, body, confirmation);
      if (response.data.authorizationUrl) {
        const url = new URL(response.data.authorizationUrl);
        if (url.protocol !== 'https:' && url.hostname !== 'localhost') throw new Error('Invalid authorization URL');
        sessionStorage.setItem('mcp-link-state', url.searchParams.get('state'));
        window.location.assign(url.toString());
        return;
      }
      await load();
      setMessage('Cambios guardados. Los permisos se aplican a partir de la siguiente llamada del conector.');
    } catch (e) { setError(e.response?.data?.message || 'No se pudo guardar el cambio.'); }
    finally { setBusy(false); }
  }

  async function changeTools(tools, granted) {
    if (busy || !tools.length) return;
    const details = tools.map(tool => toolPresentation(tool).title + ' (versión ' + tool.consentVersion + '). ' +
      (tool.access === 'write' ? 'Podrá modificar: ' : 'Leerá: ') + tool.data.join(', ') + '. ' + toolPresentation(tool).description).join('\n\n');
    if (!window.confirm((granted ? '¿Permitir estas funciones?' : '¿Revocar el permiso de estas funciones?') + '\n\n' + details)) return;
    setBusy(true); setError(''); setMessage('');
    let saved = 0;
    try {
      // The API grants each current tool explicitly, never a category or future tools.
      for (const tool of tools) {
        await api.put(ROOT + '/tools/' + tool.id, { granted, consentVersion: tool.consentVersion }, confirmation);
        saved += 1;
      }
      await load();
      setMessage('Cambios guardados. Los permisos se aplican a partir de la siguiente llamada del conector.');
    } catch (e) {
      await load();
      setError((saved ? 'Se guardaron ' + saved + ' de ' + tools.length + ' cambios. ' : '') + (e.response?.data?.message || 'No se pudo guardar el cambio.'));
    } finally { setBusy(false); }
  }

  return (
    <div className="app-shell">
      <AppShellNav />
      <main className="app-shell-main">
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>Acceso MCP</Typography>
          <Typography paragraph>Controla las funciones que ChatGPT y Claude pueden usar con tu cuenta DevsSoft.</Typography>
          <Typography paragraph color="text.secondary">Vincular Google, tener una suscripción y conceder acceso MCP son decisiones independientes.</Typography>
          <Button href="/account/mcp-guide" variant="outlined" sx={{ mb: 3 }}>Cómo conectar Devssoft a ChatGPT Work</Button>
          {error && <Alert severity="error" role="alert" sx={{ mb: 2 }}>{error}<Button onClick={load} disabled={busy}>Reintentar</Button></Alert>}
          {message && <Alert severity="success" role="status" sx={{ mb: 2 }}>{message}</Alert>}
          {!access ? <Box role="status" aria-label="Cargando permisos"><CircularProgress /></Box> : (
            <Stack spacing={3} aria-busy={busy}>
              {access.servers.filter(server => !server.public).map(server => (
                <Card key={server.id} variant="outlined">
                  <CardContent>
                    <Typography component="h2" variant="h5" gutterBottom>{server.name}</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography>Google: {access.linked ? 'Vinculado' : 'Sin vincular'}</Typography>
                      {access.linked
                        ? <Button disabled={busy} color="error" onClick={() => mutate('/link', null, '¿Desvincular Google y revocar todos los permisos MCP?', 'delete')}>Desvincular Google</Button>
                        : <Button disabled={busy} onClick={() => mutate('/link/start', {}, '¿Vincular tu cuenta Google a esta cuenta DevsSoft? No se concederá acceso a las funciones automáticamente.', 'post')}>Vincular Google</Button>}
                    </Stack>
                    <FormControlLabel sx={{ mb: 2 }} label={access.enabled ? 'Acceso MCP activado' : 'Acceso MCP desactivado'} control={
                      <Switch checked={access.enabled} disabled={busy || !access.linked} onChange={(_, enabled) => mutate('/servers/devssoft', { enabled }, enabled ? '¿Activar MCP? Luego podrás conceder cada función.' : '¿Revocar todo el acceso MCP? Deberás conceder nuevamente las funciones al reactivarlo.')} />
                    } />
                    <Stack spacing={1}>
                      {['read', 'write'].map(type => <PermissionSection key={type} serverId={server.id} accessType={type}
                        tools={server.tools.filter(tool => !tool.base && (tool.access || 'read') === type)} access={access} busy={busy} onChange={changeTools} />)}
                      {server.tools.some(tool => tool.base) && <Accordion disableGutters variant="outlined" TransitionProps={{ unmountOnExit: true }}>
                        <AccordionSummary aria-label="Funciones básicas" expandIcon={<ExpandMoreIcon />} id={server.id + '-base-header'} aria-controls={server.id + '-base-content'}>
                          <Box><Typography component="h3" variant="subtitle1">Funciones básicas</Typography><Typography variant="body2" color="text.secondary">Disponibles mientras el acceso MCP esté activo</Typography></Box>
                        </AccordionSummary>
                        <AccordionDetails id={server.id + '-base-content'}>{server.tools.filter(tool => tool.base).map(tool => <ToolRow key={tool.id} tool={tool} />)}</AccordionDetails>
                      </Accordion>}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              {access.servers.filter(server => server.public).map(server => (
                <Accordion key={server.id} disableGutters variant="outlined" TransitionProps={{ unmountOnExit: true }}>
                  <AccordionSummary aria-label="Herramientas públicas" expandIcon={<ExpandMoreIcon />} id={server.id + '-header'} aria-controls={server.id + '-content'}>
                    <Box><Typography component="h2" variant="h6">Herramientas públicas</Typography><Typography variant="body2" color="text.secondary">Sin acceso a tu cuenta · {server.tools.length} funciones</Typography></Box>
                  </AccordionSummary>
                  <AccordionDetails id={server.id + '-content'}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Públicas; no requieren acceso a tu cuenta.</Typography>
                    {server.tools.map(tool => <ToolRow key={tool.id} tool={tool} publicTool />)}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Container>
      </main>
    </div>
  );
}
